// The ad reference (GG-2/GG-3, approval 381): which ad sent a buyer, read from
// the page's own URL when a buy, trial or login button is clicked, and kept on
// our own records (Stripe metadata `adref`, PostHog server events) whatever
// the consent choice. Never stored on the device, never a click id (only
// whether one was there), never sent to an ad platform. Pure and isomorphic:
// the buttons, the server and the landing count
// (src/lib/server/landing-count.ts) all use these rules. Switched by
// AD_REF_ON_ORDERS on the server (src/lib/server/ad-ref-orders.ts).
// Failure modes: tests/ad-ref-orders/failure-modes.md.

// Label-shaped: our campaign tags, {{…}} macros and bare Meta/Google ids. No
// space, "@", "/", ":", "=", "%" (double-encoded text) or non-ASCII, which
// stops sentences, e-mails and spaced names (not a one-word name).
const UTM_LABEL = /^[A-Za-z0-9_.+{}|-]+$/;

/**
 * The landing count's UTMs: trimmed and cut to 150 characters, then kept only
 * when label-shaped, else "other" (landing-count failure mode 19). Blank is null.
 */
export function utmValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim().slice(0, 150);
  if (!trimmed) return null;
  return UTM_LABEL.test(trimmed) ? trimmed : "other";
}

// The ad platforms' own numeric ids (Meta utm_id/ad_id/adset_id; Google
// gad_campaignid, and utm_adgroup from our tracking template): ASCII digits
// only, 1–20 characters after trimming, kept as the exact string (18-digit
// Meta ids exceed 2^53); anything else is null, never "other". No phone
// filter: our ids are 11–18 digits (landing-count failure mode 20).
const AD_PLATFORM_ID = /^[0-9]{1,20}$/;

export function adPlatformId(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && AD_PLATFORM_ID.test(trimmed) ? trimmed : null;
}

/** Numeric ad ids, digits only. */
const ID_KEYS = ["ad_id", "adset_id", "utm_id", "gad_campaignid", "utm_adgroup"] as const;
/**
 * Labels with their caps: longer is "other", not cut, so the packed metadata
 * fits Stripe's 500 characters (474 at every cap; failure mode 2).
 */
const LABEL_CAPS = {
  utm_source: 24,
  utm_medium: 24,
  utm_campaign: 64,
  utm_content: 64,
} as const;
const LABEL_KEYS = Object.keys(LABEL_CAPS) as (keyof typeof LABEL_CAPS)[];
/** Whether a click id was in the URL: the value itself is never read (failure mode 6). */
const FLAG_KEYS = ["has_gclid", "has_fbclid"] as const;

type IdKey = (typeof ID_KEYS)[number];
type LabelKey = keyof typeof LABEL_CAPS;
type FlagKey = (typeof FLAG_KEYS)[number];

export type AdRef = Partial<Record<IdKey | LabelKey, string>> & Partial<Record<FlagKey, true>>;

/** The one in-site URL parameter that carries an ad reference (login hop, cancelled checkout). */
export const AD_REF_URL_PARAM = "gg_adref";
/** The Stripe metadata key: one key, so a session stays within 50 keys (failure mode 1). */
export const AD_REF_METADATA_KEY = "adref";
/** The longest query string the server reads (failure mode 8). */
const MAX_QUERY_LENGTH = 1_000;
const MAX_METADATA_LENGTH = 500;

function labelValue(value: string | null, cap: number): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.length <= cap && UTM_LABEL.test(trimmed) ? trimmed : "other";
}

function nonEmpty(adRef: AdRef): AdRef | null {
  return Object.keys(adRef).length ? adRef : null;
}

/** The ad reference in a page URL's own parameters (no carry): the raw keys only. */
function fromLandingParams(params: URLSearchParams): AdRef | null {
  const adRef: AdRef = {};
  // The first value of a repeated key, as the landing count reads it.
  for (const key of ID_KEYS) {
    const value = adPlatformId(params.get(key));
    if (value) adRef[key] = value;
  }
  for (const key of LABEL_KEYS) {
    const value = labelValue(params.get(key), LABEL_CAPS[key]);
    if (value) adRef[key] = value;
  }
  if (["gclid", "gbraid", "wbraid"].some((key) => Boolean(params.get(key)?.trim()))) adRef.has_gclid = true;
  if (params.get("fbclid")?.trim()) adRef.has_fbclid = true;
  return nonEmpty(adRef);
}

/** An ad reference's own query form (`ad_id=…&has_fbclid=1`), re-cleaned key by key. */
function fromAdRefParams(params: URLSearchParams): AdRef | null {
  const adRef: AdRef = {};
  for (const key of ID_KEYS) {
    const value = adPlatformId(params.get(key));
    if (value) adRef[key] = value;
  }
  for (const key of LABEL_KEYS) {
    const value = labelValue(params.get(key), LABEL_CAPS[key]);
    if (value) adRef[key] = value;
  }
  for (const key of FLAG_KEYS) if (params.get(key) === "1") adRef[key] = true;
  return nonEmpty(adRef);
}

/**
 * Untrusted input (a server action argument, a `gg_adref` value): a string of
 * at most 1,000 characters in the query form, parsed and cleaned. Anything
 * else is null; never throws (failure mode 8).
 */
export function sanitizeAdRef(query: unknown): AdRef | null {
  try {
    if (typeof query !== "string" || !query || query.length > MAX_QUERY_LENGTH) return null;
    return fromAdRefParams(new URLSearchParams(query));
  } catch {
    return null;
  }
}

/** The query form, keys in a fixed order: `gg_adref`'s value and the buttons' argument. */
export function adRefQuery(adRef: AdRef | null): string | undefined {
  if (!adRef) return undefined;
  const params = new URLSearchParams();
  for (const key of [...ID_KEYS, ...LABEL_KEYS]) {
    const value = adRef[key];
    if (value) params.set(key, value);
  }
  for (const key of FLAG_KEYS) if (adRef[key]) params.set(key, "1");
  const query = params.toString();
  return query || undefined;
}

/** Whether an ad reference names an ad or a campaign, not only that a click id was there. */
function namesAd(adRef: AdRef | null): adRef is AdRef {
  return Boolean(adRef && [...ID_KEYS, ...LABEL_KEYS].some((key) => adRef[key]));
}

/**
 * The page's ad reference at click time, as the query form, from one source
 * only (failure mode 16): the URL's own keys; else a fragment query
 * (`#pricing?utm_…`, the DK Google template); else the carried `gg_adref`.
 * A source holding only a click id flag (an fbclid with no ad ids) counts
 * last, so it never hides a carried reference that names the ad. Undefined
 * when there's none or on any error (failure mode 3).
 */
export function readAdRefFromLocation(
  location: { search?: string; hash?: string } | null | undefined,
): string | undefined {
  try {
    if (!location) return undefined;
    const search = new URLSearchParams(location.search ?? "");
    const fragmentQuery = /^#[^?&]*[?&](.*)$/.exec(location.hash ?? "")?.[1];
    const sources = [
      fromLandingParams(search),
      fragmentQuery ? fromLandingParams(new URLSearchParams(fragmentQuery)) : null,
      sanitizeAdRef(search.get(AD_REF_URL_PARAM)),
    ];
    return adRefQuery(sources.find(namesAd) ?? sources.find((source) => source !== null) ?? null);
  } catch {
    return undefined;
  }
}

/**
 * A same-site path without its `gg_adref`, for addresses that leave our site
 * (the magic link in an e-mail, Google sign-in's OAuth state). Unchanged when
 * it has none.
 */
export function withoutAdRefParam(path: string): string {
  try {
    if (!path.includes(AD_REF_URL_PARAM)) return path;
    const url = new URL(path, "https://guest.gallery");
    if (!url.searchParams.has(AD_REF_URL_PARAM)) return path;
    url.searchParams.delete(AD_REF_URL_PARAM);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path;
  }
}

/** The carried `gg_adref` of a same-site path (`next`), and only that (GG-3). */
export function readCarriedAdRef(path: string | null | undefined): AdRef | null {
  try {
    if (!path || !path.startsWith("/") || path.startsWith("//")) return null;
    return sanitizeAdRef(new URL(path, "https://guest.gallery").searchParams.get(AD_REF_URL_PARAM));
  } catch {
    return null;
  }
}

/**
 * The Stripe metadata value: compact JSON, written only when a field survived.
 * Label-safe values need no escaping; if it's ever over 500 characters,
 * utm_content then utm_campaign go first (failure mode 2).
 */
export function packAdRefMetadata(adRef: AdRef | null): string | null {
  if (!adRef) return null;
  const fields: Record<string, string | number> = {};
  for (const key of [...ID_KEYS, ...LABEL_KEYS]) {
    const value = adRef[key];
    if (value) fields[key] = value;
  }
  for (const key of FLAG_KEYS) if (adRef[key]) fields[key] = 1;
  if (!Object.keys(fields).length) return null;
  const pack = (): string => JSON.stringify({ v: 1, src: "url_at_click", ...fields });
  for (const drop of ["utm_content", "utm_campaign"]) {
    if (pack().length <= MAX_METADATA_LENGTH) break;
    delete fields[drop];
  }
  const value = pack();
  return value.length <= MAX_METADATA_LENGTH ? value : null;
}

/** The ad reference stored on a Stripe session, re-cleaned; null when absent or malformed (failure mode 13). */
export function parseAdRefMetadata(value: unknown): AdRef | null {
  try {
    if (typeof value !== "string" || !value || value.length > MAX_METADATA_LENGTH) return null;
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const record = parsed as Record<string, unknown>;
    if (record.v !== 1) return null;
    const params = new URLSearchParams();
    for (const key of [...ID_KEYS, ...LABEL_KEYS]) {
      if (Object.prototype.hasOwnProperty.call(record, key) && typeof record[key] === "string") {
        params.set(key, record[key] as string);
      }
    }
    for (const key of FLAG_KEYS) {
      if (Object.prototype.hasOwnProperty.call(record, key) && record[key] === 1) params.set(key, "1");
    }
    return fromAdRefParams(params);
  } catch {
    return null;
  }
}

export type AdRefSource = "url_at_click" | "consented_storage" | "none";

/**
 * Event properties: `adref_<key>` (ids as strings, flags as true) plus
 * `ad_ref_source`. They never reuse the consent-gated attribution names
 * (`ad_id`, `utm_*`, `attribution_status`), which keep their meaning
 * (failure mode 12). `consentedCampaign`: the consented attribution already
 * names a campaign or an ad.
 */
export function adRefProperties(
  adRef: AdRef | null,
  consentedCampaign = false,
): Record<string, string | boolean> {
  const properties: Record<string, string | boolean> = {
    ad_ref_source: adRef ? "url_at_click" : consentedCampaign ? "consented_storage" : "none",
  };
  if (!adRef) return properties;
  for (const key of [...ID_KEYS, ...LABEL_KEYS]) {
    const value = adRef[key];
    if (value) properties[`adref_${key}`] = value;
  }
  for (const key of FLAG_KEYS) if (adRef[key]) properties[`adref_${key}`] = true;
  return properties;
}

/** Whether consented attribution properties (getAttributionPropertiesFromStripeMetadata) name a campaign or an ad. */
export function hasConsentedCampaign(attribution: Record<string, string>): boolean {
  return ["utm_campaign", "utm_content", "utm_id", "ad_id", "adset_id", "gad_campaignid"].some(
    (key) => Boolean(attribution[key]),
  );
}
